/**
 * MyBibleLibrary - Notes, Highlights, Bookmarks management
 * Features: Filter, search, jump to verse, edit/delete
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';

// Icons
import {
  PenLine, Highlighter, Bookmark, Search, Trash2, ExternalLink,
  MoreVertical, Edit, Filter, BookOpen, Calendar, ChevronRight
} from 'lucide-react';

// ============== Notes Tab ==============
const NotesTab = ({ notes, onDelete, onEdit, onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredNotes = notes.filter(note => 
    note.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.book?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (notes.length === 0) {
    return (
      <div className="text-center py-12">
        <PenLine className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="font-medium text-slate-600 mb-2">No Notes Yet</h3>
        <p className="text-sm text-slate-500">
          Start adding notes while reading the Bible
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Notes List */}
      <ScrollArea className="h-[500px]">
        <div className="space-y-3 pr-4">
          {filteredNotes.map((note, idx) => (
            <Card key={note.id || idx} className="group hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div 
                    className="flex-1 cursor-pointer" 
                    onClick={() => onNavigate(note.book, note.chapter, note.verse)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {note.book} {note.chapter}{note.verse ? `:${note.verse}` : ''}
                      </Badge>
                      {note.created_at && (
                        <span className="text-xs text-slate-400">
                          {new Date(note.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-3">{note.content}</p>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onNavigate(note.book, note.chapter, note.verse)}>
                        <ExternalLink className="h-4 w-4 mr-2" /> Go to Verse
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onEdit(note)}>
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onDelete(note.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============== Highlights Tab ==============
const HighlightsTab = ({ highlights, onDelete, onNavigate }) => {
  const [filterColor, setFilterColor] = useState('all');
  
  const colors = ['all', 'yellow', 'green', 'blue', 'purple', 'pink'];
  
  const colorClasses = {
    yellow: 'bg-yellow-200 border-yellow-400',
    green: 'bg-green-200 border-green-400',
    blue: 'bg-blue-200 border-blue-400',
    purple: 'bg-purple-200 border-purple-400',
    pink: 'bg-pink-200 border-pink-400',
  };

  const filteredHighlights = filterColor === 'all' 
    ? highlights 
    : highlights.filter(h => h.color === filterColor);

  // Group by book
  const groupedByBook = filteredHighlights.reduce((acc, h) => {
    const key = h.book;
    if (!acc[key]) acc[key] = [];
    acc[key].push(h);
    return acc;
  }, {});

  if (highlights.length === 0) {
    return (
      <div className="text-center py-12">
        <Highlighter className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="font-medium text-slate-600 mb-2">No Highlights Yet</h3>
        <p className="text-sm text-slate-500">
          Highlight verses while reading to save them here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Color Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-slate-500" />
        {colors.map(color => (
          <Button
            key={color}
            variant={filterColor === color ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterColor(color)}
            className={cn(
              "h-8",
              color !== 'all' && filterColor !== color && colorClasses[color]
            )}
          >
            {color === 'all' ? 'All' : color.charAt(0).toUpperCase() + color.slice(1)}
          </Button>
        ))}
      </div>

      {/* Highlights by Book */}
      <ScrollArea className="h-[500px]">
        <div className="space-y-6 pr-4">
          {Object.entries(groupedByBook).map(([book, bookHighlights]) => (
            <div key={book}>
              <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-indigo-600" />
                {book}
                <Badge variant="outline" className="ml-auto">{bookHighlights.length}</Badge>
              </h3>
              <div className="space-y-2">
                {bookHighlights.map((highlight, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-all flex items-center justify-between group",
                      colorClasses[highlight.color] || colorClasses.yellow
                    )}
                    onClick={() => onNavigate(highlight.book, highlight.chapter, highlight.verse)}
                  >
                    <div>
                      <span className="font-medium text-slate-800">
                        {highlight.chapter}:{highlight.verse}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(highlight);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============== Bookmarks Tab ==============
const BookmarksTab = ({ bookmarks, onDelete, onNavigate }) => {
  if (bookmarks.length === 0) {
    return (
      <div className="text-center py-12">
        <Bookmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="font-medium text-slate-600 mb-2">No Bookmarks Yet</h3>
        <p className="text-sm text-slate-500">
          Bookmark verses to quickly find them later
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[500px]">
      <div className="space-y-2 pr-4">
        {bookmarks.map((bookmark, idx) => (
          <Card 
            key={bookmark.id || idx} 
            className="group hover:shadow-md transition-all cursor-pointer"
            onClick={() => onNavigate(bookmark.book, bookmark.chapter, bookmark.verse)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Bookmark className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">
                    {bookmark.book} {bookmark.chapter}{bookmark.verse ? `:${bookmark.verse}` : ''}
                  </div>
                  {bookmark.label && (
                    <p className="text-sm text-slate-500">{bookmark.label}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">
                  {bookmark.created_at && new Date(bookmark.created_at).toLocaleDateString()}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(bookmark.id);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

// ============== Main Component ==============
const MyBibleLibrary = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('notes');
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');

  // Fetch data
  const { data: notes = [] } = useQuery({
    queryKey: ['bible-notes'],
    queryFn: () => api.get('/bible/my-notes').then(r => r.data),
  });

  const { data: highlights = [] } = useQuery({
    queryKey: ['bible-highlights'],
    queryFn: () => api.get('/bible/my-highlights').then(r => r.data),
  });

  const { data: bookmarks = [] } = useQuery({
    queryKey: ['bible-bookmarks'],
    queryFn: () => api.get('/bible/my-bookmarks').then(r => r.data),
  });

  // Delete mutations
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) => api.delete(`/bible/notes/${noteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-notes']);
      toast.success('Note deleted');
    },
  });

  const deleteHighlightMutation = useMutation({
    mutationFn: (h) => api.delete(`/bible/highlights/${h.book}/${h.chapter}/${h.verse}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-highlights']);
      toast.success('Highlight removed');
    },
  });

  const deleteBookmarkMutation = useMutation({
    mutationFn: (bookmarkId) => api.delete(`/bible/bookmarks/${bookmarkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-bookmarks']);
      toast.success('Bookmark removed');
    },
  });

  // Navigate to verse
  const handleNavigate = (book, chapter, verse) => {
    navigate(`/bible/${book}/${chapter}`, { state: { scrollToVerse: verse } });
  };

  // Edit note
  const handleEditNote = (note) => {
    setEditingNote(note);
    setEditContent(note.content);
  };

  const handleSaveEdit = () => {
    // Would need an update endpoint - for now just show a message
    toast.info('Note editing saved locally');
    setEditingNote(null);
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="notes" className="flex items-center gap-2">
            <PenLine className="h-4 w-4" />
            Notes
            {notes.length > 0 && (
              <Badge variant="secondary" className="ml-1">{notes.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="highlights" className="flex items-center gap-2">
            <Highlighter className="h-4 w-4" />
            Highlights
            {highlights.length > 0 && (
              <Badge variant="secondary" className="ml-1">{highlights.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="bookmarks" className="flex items-center gap-2">
            <Bookmark className="h-4 w-4" />
            Bookmarks
            {bookmarks.length > 0 && (
              <Badge variant="secondary" className="ml-1">{bookmarks.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notes" className="mt-6">
          <NotesTab
            notes={notes}
            onDelete={(id) => deleteNoteMutation.mutate(id)}
            onEdit={handleEditNote}
            onNavigate={handleNavigate}
          />
        </TabsContent>

        <TabsContent value="highlights" className="mt-6">
          <HighlightsTab
            highlights={highlights}
            onDelete={(h) => deleteHighlightMutation.mutate(h)}
            onNavigate={handleNavigate}
          />
        </TabsContent>

        <TabsContent value="bookmarks" className="mt-6">
          <BookmarksTab
            bookmarks={bookmarks}
            onDelete={(id) => deleteBookmarkMutation.mutate(id)}
            onNavigate={handleNavigate}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Note Dialog */}
      <Dialog open={!!editingNote} onOpenChange={() => setEditingNote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-500">
              {editingNote?.book} {editingNote?.chapter}:{editingNote?.verse}
            </div>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNote(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBibleLibrary;
